import { withHistory } from "@common/util/withHistory";
import { Point, Size } from "./Renderer/types";
import {
  CurrentAction,
  ISelection,
  PageSaveState,
  PageState,
  State,
  TaxonomyMode,
} from "./StateManager/state";
import { ApiPage, ApiZone, Page, Zone, ZoneDimensions } from "./types";

export const isPointInZone = (point: Point) => (zone: Zone): boolean =>
  point.x >= zone.minX && point.x <= zone.maxX && point.y >= zone.minY && point.y <= zone.maxY;

export const rectanglesOverlap = (
  r1x0: number,
  r1x1: number,
  r1y0: number,
  r1y1: number,
  r2x0: number,
  r2x1: number,
  r2y0: number,
  r2y1: number,
): boolean => {
  // if r1 is above r2 completely, return false
  if (r1y1 <= r2y0) {
    return false;
  }
  // if r1 is below r2 completely, return false
  if (r1y0 >= r2y1) {
    return false;
  }
  // if r1 is left of r2 completely, return false
  if (r1x1 <= r2x0) {
    return false;
  }
  // if r1 is right of r2 completely, return false
  if (r1x0 >= r2x1) {
    return false;
  }
  // otherwise they must overlap
  return true;
};

export const isZoneInSelection = (selection: ISelection) => (zone: Zone): boolean =>
  rectanglesOverlap(
    Math.min(selection.endx, selection.startx),
    Math.max(selection.endx, selection.startx),
    Math.min(selection.endy, selection.starty),
    Math.max(selection.endy, selection.starty),
    zone.minX,
    zone.maxX,
    zone.minY,
    zone.maxY,
  );

export const doZonesOverlap = (zoneA: Zone) => (zoneB: Zone): boolean =>
  rectanglesOverlap(
    zoneA.minX,
    zoneA.maxX,
    zoneA.minY,
    zoneA.maxY,
    zoneB.minX,
    zoneB.maxX,
    zoneB.minY,
    zoneB.maxY,
  );

type MouseCursor = "cell" | "crosshair" | "ew-resize" | "grab" | "grabbing" | "ns-resize";

export const mapCurrentActionToCursor = (state: CurrentAction): MouseCursor => {
  switch (state) {
    case CurrentAction.CREATING_ZONE:
      return "cell";
    case CurrentAction.HOVERING_ZONE:
      return "grab";
    case CurrentAction.MOVING:
      return "grabbing";
    case CurrentAction.RESIZING_BOTTOM:
    case CurrentAction.RESIZING_TOP:
      return "ns-resize";
    case CurrentAction.RESIZING_LEFT:
    case CurrentAction.RESIZING_RIGHT:
      return "ew-resize";
    case CurrentAction.HOVERING_NOTHING:
    case CurrentAction.SELECTING:
    default:
      return "crosshair";
  }
};

// loads zones from the API JSON file
// when loading table cells, make sure no cell is out of bound
export const mapFromApiZone = (imageDimensions: Size) => (raw: ApiZone): Zone => {
  const toReturn: Zone = {
    confidence: raw.scores,
    humanGenerated: false,
    label: raw.subclass || raw.class || "",
    maxX: Math.min(imageDimensions.width, raw.box[2]),
    maxY: Math.min(imageDimensions.height, raw.box[3]),
    minX: Math.max(0, raw.box[0]),
    minY: Math.max(0, raw.box[1]),
  };

  if (raw.class !== "table") {
    return toReturn;
  }
  let cells: Array<ZoneDimensions> = [];
  if (Array.isArray(raw.cells) && raw.cells.length > 0) {
    if (raw.nuCells) {
      cells = raw.nuCells.map(
        (c): ZoneDimensions => {
          return {
            maxX: Math.min(c.x2, toReturn.maxX),
            maxY: Math.min(c.y2, toReturn.maxY),
            minX: Math.max(c.x1, toReturn.minX),
            minY: Math.max(c.y1, toReturn.minY),
            cellType: c.cellType,
            rightBorderImplicit: c.rightBorderImplicit,
            topBorderImplicit: c.topBorderImplicit,
            bottomBorderImplicit: c.bottomBorderImplicit,
            leftBorderImplicit: c.leftBorderImplicit
          };        
        },
      );
    } else {
      cells = raw.cells.map(
        (c): ZoneDimensions => {
          if (Array.isArray(c) && c.length === 4) {
            return {
              maxX: Math.min(c[2], toReturn.maxX),
              maxY: Math.min(c[3], toReturn.maxY),
              minX: Math.max(c[0], toReturn.minX),
              minY: Math.max(c[1], toReturn.minY),
            };
          }
          throw new Error("Invalid cell bbox.");
        },
      );
    }
  }
  if (cells.length === 0) {
    // TODO : Could fail loudly instead?
    console.warn("Backend gave us a table zone with no cells")
    console.warn(raw);
    cells = [{
      maxX: toReturn.maxX,
      maxY: toReturn.maxY,
      minX: toReturn.minX,
      minY: toReturn.minY,
    }]
  }
  return { ...toReturn, cells };
};

export const zoneDimensionsValid = (imageDimensions: Size, taxonomy: State["taxonomy"]) => (
  zone: Zone,
): boolean => {
  if (
    zone.minX >= 0 &&
    zone.minX <= imageDimensions.width &&
    zone.maxX > zone.minX &&
    zone.maxX <= imageDimensions.width &&
    zone.minY >= 0 &&
    zone.minY <= imageDimensions.height &&
    zone.maxY > zone.minY &&
    zone.maxY <= imageDimensions.height
  ) {
    if (isTableLabel(zone.label, taxonomy) && Array.isArray(zone.cells)) {
      for (const cell of zone.cells) {
        if (
          cell.minX >= zone.minX &&
          cell.minX <= zone.maxX &&
          cell.maxX > cell.minX &&
          cell.maxX <= zone.maxX &&
          cell.minY >= zone.minY &&
          cell.minY <= zone.maxY &&
          cell.maxY > cell.minY &&
          cell.maxY <= zone.maxY
        ) {
          continue;
        }
        console.warn("Invalid zone and cell:", zone, cell);
        throw new Error("Table cell dimension out of bounds. Please report this error.");
      }
    }
    return true;
  }
  console.error("Invalid zone:", zone);
  throw new Error("Zone dimension out of bounds or zone invalid. Please report this error.");
};

// saves zones to the API JSON file
// when saving table cells, make sure no cell is out of bound... we've seen 14th decimal rounding bug here 
// https://github.com/innodatalabs/ilabs.goldengate.workbenches/issues/56
const mapToApiZone = (taxonomy: State["taxonomy"]) => (zone: Zone): ApiZone => {

  const toReturn: ApiZone = {
    box: [zone.minX, zone.minY, zone.maxX, zone.maxY],
    class: taxonomy.rawLabels[zone.label] ? taxonomy.rawLabels[zone.label].group : "",
    scores: zone.confidence,
    subclass: zone.label,
  };

  if (!isTableLabel(toReturn.subclass, taxonomy) || !Array.isArray(zone.cells)) {
    return toReturn;
  }

  // For some reason we've seen docuemtns end up with tables with no cells.
  // This breaks further down the pipeline.  Although i haven't found any code path
  // that can produce this outcome, let's just check for empty tables and fix them here.
  if (isTableLabel(toReturn.subclass, taxonomy)) {
    if (!zone.cells || zone.cells.length === 0) {
      zone.cells = [
        {
          minY: zone.minY,
          maxY: zone.maxY,
          maxX: zone.maxX,
          minX: zone.minX
        }
      ]
    }
  }

  return {
    ...toReturn,
    cells: zone.cells.map(c => {
      let theCell : [number,number,number,number] = [
        Math.max(c.minX, zone.minX), 
        Math.max(c.minY, zone.minY), 
        Math.min(c.maxX, zone.maxX), 
        Math.min(c.maxY, zone.maxY)
      ];

      if (!!theCell.find(x => x === undefined || x === null || x == NaN || x == Infinity || x == -Infinity)) {
        console.error("Cell with bad borders found")
        console.error("Zone : ")
        console.error(zone);
        console.error("Cell : ")
        console.error(c);
        throw Error("Bad cell encountered.  Changes to this page were not saved.  Please refresh the workbench.");
      }
      return theCell;
    }),
    nuCells: zone.cells.map(c => {
      return {
        x1: Math.max(c.minX, zone.minX),
        x2: Math.min(c.maxX, zone.maxX),
        y1: Math.max(c.minY, zone.minY),
        y2: Math.min(c.maxY, zone.maxY),
        cellType: c.cellType,
        rightBorderImplicit: c.rightBorderImplicit,
        topBorderImplicit: c.topBorderImplicit,
        bottomBorderImplicit: c.bottomBorderImplicit,
        leftBorderImplicit: c.leftBorderImplicit
      };
    })
  };
};

export const mapToApiPage = (taxonomy: State["taxonomy"]) => (page: Page): ApiPage => ({
  page_confidence: 1.0,
  predictions: page.zones.map(mapToApiZone(taxonomy)),
});

export const newPage = (
  index: number,
  dimensions: Size,
  apiZones: Array<ApiZone>,
  taxonomy: State["taxonomy"],
): PageState => {

  const zones = apiZones
    .map(mapFromApiZone(dimensions))
    .filter(zoneDimensionsValid(dimensions, taxonomy));
  return {
    activeZones: [],
    currentAction: CurrentAction.HOVERING_NOTHING,
    dimensions,
    firstDragEvent: false,
    hoveredZone: -1,
    image: null,
    index,
    noiseReductionLevel: -1,
    pageSaveState: PageSaveState.Clean,
    previousMouseLocation: null,
    selection: null,
    zoneHistory: withHistory(zones),
    zones,
  };
};

export const getResizeHandlerPositionsForZone = (zone: Zone): [Point, Point, Point, Point] => {
  const middle: Point = {
    x: (zone.minX + zone.maxX) / 2,
    y: (zone.minY + zone.maxY) / 2,
  };
  // return [TOP, RIGHT, BOTTOM, LEFT]
  return [
    {
      x: middle.x,
      y: zone.minY,
    },
    {
      x: zone.maxX,
      y: middle.y,
    },
    {
      x: middle.x,
      y: zone.maxY,
    },
    {
      x: zone.minX,
      y: middle.y,
    },
  ];
};

const square = (x: number): number => x * x;

export const isPointWithinRadiusOf = (pointA: Point, radius: number) => (pointB: Point): boolean =>
  square(pointA.x - pointB.x) + square(pointA.y - pointB.y) <= square(radius);

type RGB = [number, number, number];
// the offsets for red, green, and blue in the RGB type
const RED = 0;
const GREEN = 1;
const BLUE = 2;

/**
 * Computes the "distance" between two colors in the RGB-space. This algorithm is a very simple
 * Euclidean distance (sqrt(deltaR^2 + deltaG^2 + deltaB^2)). The algorithm applies even weights
 * to the three components, though research suggests trying weights of 2, 4 and 3 against red,
 * green, and blue respectively. It is not clear if human differentiation of color is a good measure
 * for noise removal.
 * @param a RGB Pixel A in RGB format.
 * @param b RGB Pixel B in RGB format.
 * @return number Returns the distance in Euclidean space between two colors. A zero value indicates
 *         the two colors are the same.
 */
const colorDistance = (a: RGB, b: RGB): number =>
  Math.sqrt(square(a[RED] - b[RED]) + square(a[GREEN] - b[GREEN]) + square(a[BLUE] - b[BLUE]));

/**
 * Returns a method that can tell if a given pixel is within the threshold of the given bgColor.
 * @param bgColor RGB The given background color.
 * @param threshold number The color distance threshold. Set to 0 for a strict color test.
 * @return (RGB => boolean) Returns a function that takes a single pixel and returns true if the
 *         given pixel is further from the background color than the threshold.
 */
const isNotBackground = (bgColor: RGB, threshold: number) => (pixel: RGB): boolean =>
  colorDistance(bgColor, pixel) > threshold;

const BYTES_PER_PIXEL = 4;
/**
 * Returns the RGB value of the pixel at the requested coordinate. To reduce the noisiness of the
 * colors, the color value of the pixel is deflated and then re-inflated in a lossy manner.
 * @param imageData ImageData The input image data.
 * @param x number The x-value for the pixel.
 * @param y number The y-value for the pixel.
 * @param noiseReduction number The number of bits to compress. Must be an integer between 0 and 7
 *                              inclusive. 0 indicates no noise reduction. For documents with a pure
 *                              background, 0 will provide the best results, but a value of 2 or 3
 *                              seems to experimentally work best for documents with noisy
 *                              backgrounds.
 * @return RGB Returns the RGB value of the pixel for the coordinate in [red, green, blue] byte
 *         array.
 */
const rbgAtXAndY = (imageData: ImageData, x: number, y: number, noiseReduction: number): RGB => {
  const offset = y * BYTES_PER_PIXEL * imageData.width + x * BYTES_PER_PIXEL;
  const compress = Math.pow(2, noiseReduction);
  return [
    Math.floor(imageData.data[offset] / compress) * compress,
    Math.floor(imageData.data[offset + 1] / compress) * compress,
    Math.floor(imageData.data[offset + 2] / compress) * compress,
  ];
};

/**
 * Returns a slice of the image data along a single vertical line (i.e. y is fixed).
 * @param imageData ImageData The input image data.
 * @param noiseReduction number The noise reduction level to use.
 * @param y number The y-value for the vertical line.
 * @param start number (optional) The start value for the x value. Defaults to 0.
 * @param end number (optional) The end value for the x value. Defaults to imageData.width - 1.
 * @return Array<RGB> Returns the array of pixels along the vertical line. Each pixel is expressed
 *         in the [red, green, blue] byte array format.
 */
const horizontalSlice = (
  imageData: ImageData,
  noiseReduction: number,
  y: number,
  start: number = 0,
  end: number = imageData.width - 1,
): Array<RGB> => {
  const slice = [];
  for (let x = start; x <= end; x++) {
    slice.push(rbgAtXAndY(imageData, x, y, noiseReduction));
  }
  return slice;
};

/**
 * Returns a slice of the image data along a single horizontal line (i.e. x is fixed).
 * @param imageData ImageData The input image data.
 * @param noiseReduction number The noise reduction level to use.
 * @param x number The x-value for the horizontal line.
 * @param start number (optional) The start value for the y value. Defaults to 0.
 * @param end number (optional) The end value for the y value. Defaults to imageData.height - 1.
 * @return Array<RGB> Returns the array of pixels along the horizontal line. Each pixel is expressed
 *         in the [red, green, blue] byte array format.
 */
const verticalSlice = (
  imageData: ImageData,
  noiseReduction: number,
  x: number,
  start: number = 0,
  end: number = imageData.height - 1,
): Array<RGB> => {
  const slice = [];
  for (let y = start; y <= end; y++) {
    slice.push(rbgAtXAndY(imageData, x, y, noiseReduction));
  }
  return slice;
};

interface IPixelCount {
  [rbgString: string]: number;
}
/**
 * Returns a hash map where the keys are RGB values (in comma separated string format) and the value
 * is the number of times we encountered that pixel in the array.
 * @param pixels Array<RGB> An input array of pixels in their RGB [red, green, blue] format.
 * @return IPixelCount A mapping of RGB values to their observed count.
 *
 */
const getPixelCount = (pixels: Array<RGB>): IPixelCount => {
  const pixelCounts: { [key: string]: number } = {};
  for (const pixel of pixels) {
    const key = pixel.join(",");
    if (key in pixelCounts) {
      pixelCounts[key]++;
    } else {
      pixelCounts[key] = 1;
    }
  }
  return pixelCounts;
};

/**
 * Converts a comma-separated RGB string (e.g. "251,12,192") to an RGB array [251, 12, 192].
 * For the sake of performance, we do not do extensive validation, so do not use this on user data.
 * @param input string The input RGB string.
 * @return RGB Returns an array in [red, green, blue] byte format.
 */
const stringToRGB = (input: string): RGB => {
  const split = input.split(",");
  if (split.length !== 3) {
    throw new Error("Invalid input string.");
  }
  return [
    Number.parseInt(split[RED], 10),
    Number.parseInt(split[GREEN], 10),
    Number.parseInt(split[BLUE], 10),
  ];
};

/**
 * Selects a noise reduction level given the input image.
 * @param image HTMLImageElement The input image.
 * @return number Returns a number between 0 and 7 inclusive that indicates an ideal noise reduction
 *                level for the given input image.
 */
export const selectNoiseReduction = (image: HTMLImageElement): number => {
  const height = image.naturalHeight;
  const width = image.naturalWidth;
  const canvas = document.createElement("canvas");
  if (!canvas) {
    console.error("Browser does not support canvas drawing.");
    return 0;
  }
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) {
    console.error("Browser does not support canvas drawing.");
    return 0;
  }
  context.drawImage(image, 0, 0, width, height);
  const imageData = context.getImageData(0, 0, width, height);
  const borderPixelCounts = getPixelCount([
    ...verticalSlice(imageData, 0, 0), // left border
    ...verticalSlice(imageData, 0, imageData.width - 1), // right border
    ...horizontalSlice(imageData, 0, 0), // top border
    ...horizontalSlice(imageData, 0, imageData.height - 1), // bottom border
  ]);
  const pixels: Array<string> = Object.keys(borderPixelCounts);
  if (pixels.length > 1) {
    return 2;
  }
  return 0;
};

/**
 * Selects a background color from the image data.
 * @param imageData ImageData The input image data.
 * @param noiseReduction number The noise reduction level to use.
 * @return RGB Returns the background color in [red, green, blue] byte array format.
 */
const selectBackgroundColor = (imageData: ImageData, noiseReduction: number): RGB => {
  // take a slice of the top, bottom, left and right and collapse their pixels into a counting table
  const borderPixelCounts = getPixelCount([
    ...verticalSlice(imageData, noiseReduction, 0), // left border
    ...verticalSlice(imageData, noiseReduction, imageData.width - 1), // right border
    ...horizontalSlice(imageData, noiseReduction, 0), // top border
    ...horizontalSlice(imageData, noiseReduction, imageData.height - 1), // bottom border
  ]);
  const pixels: Array<string> = Object.keys(borderPixelCounts);
  if (pixels.length < 1) {
    throw new Error("Image does not have any pixel data.");
  } // find the pixel with the highest count
  let maxRGB = pixels[0];
  let maxCount = borderPixelCounts[maxRGB];
  for (let i = 1; i < pixels.length; i++) {
    if (borderPixelCounts[pixels[i]] <= maxCount) {
      continue;
    }
    maxRGB = pixels[i];
    maxCount = borderPixelCounts[pixels[i]];
  }
  return stringToRGB(maxRGB);
};

type TrimmedZone = [number, number, number, number];
// the coordinates for the trimmed zone
const MIN_X = 0;
const MAX_X = 1;
const MIN_Y = 2;
const MAX_Y = 3;
/**
 * Computes the zone trimming by returning a new set of [min_x, max_x, min_y, max_y].
 * @param data ImageData The raw image data.
 * @param noiseReduction number The noise reduction level to use.
 * @param bgColor RGB The background color expressed in [red, green, blue] byte array.
 * @return [number, number, number, number] Returns an array of four numbers which map to the four
 *         corners of the trimmed zone.
 */
const trimZone = (data: ImageData, noiseReduction: number, bgColor: RGB): TrimmedZone => {
  // the negative values indicates the corner is uninitialized
  const trimmedZone: TrimmedZone = [-1, -1, -1, -1];
  const isNonBackgroundPixel = isNotBackground(bgColor, 0);
  // trim the top and bottom at the same time
  let walker = 0;
  const midHeight = Math.floor(data.height / 2);
  while ((trimmedZone[MIN_Y] < 0 || trimmedZone[MAX_Y] < 0) && walker <= midHeight) {
    if (trimmedZone[MIN_Y] < 0) {
      // find the first non background pixel across this horiziontal slice
      const firstNonBgPixel = horizontalSlice(data, noiseReduction, walker).findIndex(
        isNonBackgroundPixel,
      );
      if (firstNonBgPixel >= 0) {
        // we found one, so we can stop scanning for the top border
        trimmedZone[MIN_Y] = walker;
      }
    }
    if (trimmedZone[MAX_Y] < 0) {
      const firstNonBgPixel = horizontalSlice(
        data,
        noiseReduction,
        data.height - 1 - walker,
      ).findIndex(isNonBackgroundPixel);
      if (firstNonBgPixel >= 0) {
        trimmedZone[MAX_Y] = data.height - 1 - walker;
      }
    }
    walker++;
  }
  if (trimmedZone[MIN_Y] < 0 || trimmedZone[MAX_Y] < 0) {
    throw new Error("Zone was trimmed to nothing.");
  }
  walker = 0;
  const midWidth = Math.floor(data.width / 2);
  while ((trimmedZone[MIN_X] < 0 || trimmedZone[MAX_X] < 0) && walker <= midWidth) {
    if (trimmedZone[MIN_X] < 0) {
      const firstNonBgPixel = verticalSlice(
        data,
        noiseReduction,
        walker,
        trimmedZone[MIN_Y],
        trimmedZone[MAX_Y],
      ).findIndex(isNonBackgroundPixel);
      if (firstNonBgPixel >= 0) {
        trimmedZone[MIN_X] = walker;
      }
    }
    if (trimmedZone[MAX_X] < 0) {
      const firstNonBgPixel = verticalSlice(
        data,
        noiseReduction,
        data.width - 1 - walker,
        trimmedZone[MIN_Y],
        trimmedZone[MAX_Y],
      ).findIndex(isNonBackgroundPixel);
      if (firstNonBgPixel >= 0) {
        trimmedZone[MAX_X] = data.width - 1 - walker;
      }
    }
    walker++;
  }
  if (trimmedZone[MIN_X] < 0 || trimmedZone[MAX_X] < 0) {
    throw new Error("Zone was trimmed to nothing.");
  }
  return trimmedZone;
};

/**
 * Applies a shrinking algorithm to remove unncessary border/margins from the zone.
 * @param zone IZone The input zone in the coordinate space of the image.
 * @param image HTMLImageElement The image element to source for pixels.
 * @param noiseReduction number The noise reduction level to use.
 * @param autoShrink boolean (optional) If false, this method will return the input zone and not do
 *                   any work.
 * @return IZone Returns a shrunken IZone in the same coordinate space as the input. If the input
 *         zone is malformed or we cannot shrink it for some reason, the original zone is returned.
 */
export const autoShrinkZone = (
  zone: Zone,
  image: HTMLImageElement,
  noiseReduction: number,
  autoShrink: boolean = true,
): Zone => {
  if (!autoShrink) {
    return zone;
  }
  try {
    // zones may come in as floats, but we work in whole pixels
    const maxX = Math.ceil(zone.maxX);
    const maxY = Math.ceil(zone.maxY);
    const minX = Math.floor(zone.minX);
    const minY = Math.floor(zone.minY);
    const height = maxY - minY;
    const width = maxX - minX;
    const canvas = document.createElement("canvas");
    if (!canvas) {
      console.error("Browser does not support canvas drawing.");
      return zone;
    }
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) {
      console.error("Browser does not support canvas drawing.");
      return zone;
    }
    context.drawImage(image, minX, minY, width, height, 0, 0, width, height);
    const imageData = context.getImageData(0, 0, width, height);
    const background = selectBackgroundColor(imageData, noiseReduction);
    const trimmedZone = trimZone(imageData, noiseReduction, background);
    return {
      ...zone,
      maxX: Math.ceil(zone.minX + trimmedZone[MAX_X]),
      maxY: Math.ceil(zone.minY + trimmedZone[MAX_Y]),
      minX: Math.floor(zone.minX + trimmedZone[MIN_X]),
      minY: Math.floor(zone.minY + trimmedZone[MIN_Y]),
    };
  } catch (err) {
    // if something goes wrong when trimming, just abort the shrink operation
    return zone;
  }
};

export class Color {
  public static fromString(colorString: string): Color {
    if (Color.HexPattern.test(colorString)) {
      return new Color(
        parseInt(colorString.substr(1, 2).toLowerCase(), 16),
        parseInt(colorString.substr(3, 2).toLowerCase(), 16),
        parseInt(colorString.substr(5, 2).toLowerCase(), 16),
        colorString.length > 7 ? (parseInt(colorString.substr(7, 2).toLowerCase(), 16) / 255) : 1.0
      );
    }
    if (Color.RGBAPattern.test(colorString)) {
      const split = colorString
        .substring(colorString.indexOf("(") + 1, colorString.indexOf(")"))
        .split(",");
      if (split.length === 3) {
        return new Color(parseFloat(split[0]), parseFloat(split[1]), parseFloat(split[2]));
      } else if (split.length === 4) {
        return new Color(
          parseFloat(split[0]),
          parseFloat(split[1]),
          parseFloat(split[2]),
          parseFloat(split[3]),
        );
      }
    }
    throw new Error("Invalid color string.");
  }
  private static HexPattern = /#([0-9]|[a-f]|[A-F]){6}/;
  private static RGBAPattern = /rgba?\((\d|\.|,|\s)+\)/;

  public red: number;
  public green: number;
  public blue: number;
  public alpha: number;

  public constructor(red: number, green: number, blue: number, alpha: number = 1.0) {
    this.red = this.validateColor(red);
    this.green = this.validateColor(green);
    this.blue = this.validateColor(blue);
    this.alpha = this.validateAlpha(alpha);
  }

  public copy(): Color {
    return new Color(this.red, this.green, this.blue, this.alpha);
  }

  public toHexString(): string {
    if (this.alpha === 1.0) {
      return `#${this.toHexa(this.red)}${this.toHexa(this.green)}${this.toHexa(this.blue)}`;
    }
    throw new Error("Alpha is not 1.0, cannot generate hex string.");
  }

  public toHexa(number: number): string {
    let val = number.toString(16);
    while (val.length < 2) {
      val = val + "0";
    }
    return val;
  }

  public toRGBAString(): string {
    return `rgba(${this.red},${this.green},${this.blue},${this.alpha})`;
  }

  private validateColor(input: number): number {
    const asInteger = parseInt(input.toString(10), 10);
    if (isNaN(asInteger) || input < 0 || input > 255) {
      throw new Error("Invalid color value.");
    }
    return input;
  }
  private validateAlpha(input: number): number {
    const asInteger = parseFloat(input.toString(10));
    if (isNaN(asInteger) || input < 0 || input > 1) {
      throw new Error("Invalid alpha value.");
    }
    return input;
  }
}

export const isTableLabel = (label: string, taxonomy: State["taxonomy"]): boolean => {
  if (label === "table") {
    return true;
  }
  switch (taxonomy.mode) {
    case TaxonomyMode.Complex:
      if (!taxonomy.rawLabels || !taxonomy.rawLabels[label]) {
        return false;
      }
      return taxonomy.rawLabels[label].group === "table";
    default:
      return false;
  }
};
